// A centralized server that deals with signaling to other nodes that
// a node is available for bootstrapping against.

import bin from "typed-binary";
import { binencode, u8equal } from "@/lib/binary";
import { ed25519 } from "@noble/curves/ed25519";

export const Address = bin.u8Array(32);

export enum SignalProtocolMessageType {
  HELLO = 0,
  OFFER = 1,
  ANSWER = 2,
  CANDIDATE = 3,
  VERSION = 4,
}

export const SignalProtocolMessagePayload = bin.genericEnum(
  {
    from: Address,
    timestamp: bin.u64,
  },
  {
    [SignalProtocolMessageType.HELLO]: bin.object({}),
    [SignalProtocolMessageType.OFFER]: bin.object({
      to: Address,
      sdp: bin.string,
    }),
    [SignalProtocolMessageType.ANSWER]: bin.object({
      to: Address,
      sdp: bin.string,
    }),
    [SignalProtocolMessageType.CANDIDATE]: bin.object({
      to: Address,
      candidate: bin.string,
      sdpMid: bin.string,
      sdpMLineIndex: bin.i32,
      usernameFragment: bin.string,
    }),
    [SignalProtocolMessageType.VERSION]: bin.object({
      version: bin.string,
      buildTimestamp: bin.string,
    }),
  },
);

export const SignalProtocolMessage = bin.object({
  signature: bin.u8Array(64),
  payload: SignalProtocolMessagePayload,
});

export const MAX_SIGNAL_PROTOCOL_MESSAGE_AGE = 1000 * 60 * 5;

export function assertSignalProtocolMessageValid(
  message: bin.Parsed<typeof SignalProtocolMessage>,
  rawBytes?: Uint8Array,
) {
  const payload = message.payload;
  const signature = message.signature;

  if ("to" in payload && u8equal(payload.from, payload.to)) {
    throw new Error("Signal protocol message from and to are the same");
  }

  if (payload.from.byteLength !== 32) {
    throw new Error("Signal protocol message from is not a valid address");
  }

  if ("to" in payload && payload.to.byteLength !== 32) {
    throw new Error("Signal protocol message to is not a valid address");
  }

  const age = Math.abs(Date.now() - Number(payload.timestamp));
  if (age > MAX_SIGNAL_PROTOCOL_MESSAGE_AGE) {
    throw new Error(`Signal protocol message expired`);
  }

  // Use raw payload bytes when available (schema-evolution-proof).
  // Wire format: [signature: 64 bytes][payload: N bytes]
  // During rolling deploys, client and server schemas may differ, so
  // re-encoding the decoded payload can produce different bytes than
  // what the client originally signed. Using the raw bytes avoids this.
  const payloadBytes = rawBytes
    ? rawBytes.slice(64)
    : binencode(SignalProtocolMessagePayload, payload);

  const verified = ed25519.verify(
    signature,
    payloadBytes,
    payload.from,
  );

  if (!verified) {
    throw new Error("Invalid signal protocol message signature");
  }
  return verified;
}
